import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  getIntlTranslation,
  getLabel,
  covertInCurrencyFormat,
  getVariantText,
  costStyle,
} from "../../../../../utils/helper";

import { Box, Typography, Divider, Chip } from "@mui/material";

const CategoriesAssetChart = (props) => {
  const getGroupedCost = (childRows, cost) => {
    let temp = childRows.map((m) => m[cost]);
    const val = temp.reduce((x, y) => {
      return x + y;
    }, 0);
    return val;
  };

  const getGroupedCount = useCallback(
    (childRows, value) => {
      let filterData = childRows.filter((m) => m.assetIdent === value);

      let temp = props.selectedCategory?.map((m) => {
        let tempfilter = filterData.filter((f) => f.category === m.value);
        const shortTerm = getGroupedCost(tempfilter, "shortTermCost");
        const mediumTerm = getGroupedCost(tempfilter, "mediumTermCost");
        const longTerm = getGroupedCost(tempfilter, "longTermCost");
        const total = shortTerm + mediumTerm + longTerm;
        return {
          count: tempfilter.length,
          shortTerm: shortTerm,
          mediumTerm: mediumTerm,
          longTerm: longTerm,
          total: total,
          category: m.value,
        };
      });

      let tempObject = {};
      temp?.forEach((e) => {
        tempObject[e.category] = e.total;
      });
      return tempObject;
    },
    [props.selectedCategory]
  );

  const getChartData = useMemo(() => {
    let filterData = props.data;
    if (
      !!props.selectedData[props.item.key] &&
      props.selectedData[props.item.key].length > 0
    ) {
      let selected = props.selectedData[props.item.key];
      filterData = props.data.filter((f) =>
        selected.some((t) => t.value === f[props.item.key])
      );
    }

    let temp = props.data
      .filter(
        (f, i, arr) => arr.findIndex((a) => a.assetIdent === f.assetIdent) === i
      )
      .sort((a, b) => a.assetName.localeCompare(b.assetName))
      .map((m) => {
        return {
          title: m["assetName"],
          value: m["assetName"],
          ident: m["assetIdent"],
        };
      });


    let data = temp.map((x) => {
      let collected = getGroupedCount(filterData, x.ident);
      return {
        ...collected,
        name: x.title,
      };
    });
    return data;
  }, [getGroupedCount, props.data, props.item.key, props.selectedData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const obj = payload[0].payload;
      return (
        <div
          className="custom-tooltip p-2"
          style={{ backgroundColor: "#ffffff", borderRadius: 5 }}
        >
          <Box>
            <Typography
              style={{
                fontWeight: "bold",
                width: 200,
              }}
              variant="subtitle2"
            >
              {`${label}`}
            </Typography>
          </Box>
          <Divider />
          <Box className="desc w-100">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {props.selectedCategory.map((m) => (
                <>
                  <span style={costStyle}>
                    <Typography
                      style={{
                        width: "50%",
                        fontWeight: "bold",
                        color: "#254a9a",
                      }}
                      variant="subtitle2"
                    >
                      {m.value}:&nbsp;
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      style={{ width: "50%", fontWeight: "bold" }}
                    >
                      {covertInCurrencyFormat(obj[m.value])}
                    </Typography>
                  </span>
                  <Divider />
                </>
              ))}
            </div>
          </Box>
          <Divider />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {props.data.length > 0 && (
        <>
          <div width="100%" height="100%" className="d-flex flex-column">
            <ResponsiveContainer width="100%" height={1200}>
              <BarChart
                width={900}
                height={1200}
                data={getChartData}
                margin={10}
                barCategoryGap={50}
                layout={props.layout || "horizontal"}
                onClick={(props) => {
                  props.onLabelClick({
                    type: "category",
                    value: props.activeLabel,
                  });
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {props.layout === "vertical" ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis
                      dataKey="name"
                      reversed
                      type="category"
                      tick={{
                        fontSize: 12,
                        fontWeight: "bold",
                        textWrap: "wrap",
                      }}
                      tickLine={true}
                      tickMargin={10}
                      width={230}
                    />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" />
                    <YAxis />
                  </>
                )}
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {props.selectedCategory?.map((m) => (
                  <Bar dataKey={m.value} fill={m.color} barSize={8} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </>
  );
};

CategoriesAssetChart.propTypes = {
  data: PropTypes.array,
  item: PropTypes.object,
  selectedData: PropTypes.object,
  layout: PropTypes.string,
  onLabelClick: PropTypes.func,
  selectedCategory: PropTypes.object,
};

export default CategoriesAssetChart;
